import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Ban, Flag, ShieldCheck, Zap } from 'lucide-react';
import { api, ApiError } from '../api.ts';
import { TopBar } from './TopBar.tsx';
import { CLOSED_TEXT, MESSAGE_ERRORS, clock, dayLabel, type ThreadMessage, type ThreadMeta } from '../messages.ts';

/**
 * Une conversation. Principe de la messagerie : extra et établissement ne communiquent que par l'application ; le serveur masque
 * les coordonnées, ce composant l'explique à l'écran et propose, le jour de la mission, les réponses rapides et le message urgent.
 */
export function Thread({ conversationId, embedded = false, missionPath }: { conversationId: string; embedded?: boolean; missionPath?: (missionId: string) => string }) {
  const [meta, setMeta] = useState<ThreadMeta | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [missing, setMissing] = useState(false);
  const [text, setText] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'info' | 'error'; text: string } | null>(null);
  const lastSeq = useRef(0);
  const bottom = useRef<HTMLDivElement>(null);

  const markRead = useCallback(() => {
    void fetch(`/api/messages/conversations/${conversationId}/read`, { method: 'POST', credentials: 'include' });
  }, [conversationId]);

  const pull = useCallback(async (initial: boolean) => {
    try {
      const res = await api.get<{ conversation: ThreadMeta; messages: ThreadMessage[] }>(`/messages/conversations/${conversationId}?after=${initial ? 0 : lastSeq.current}`);
      setMeta(res.conversation);
      if (res.messages.length > 0) {
        lastSeq.current = Math.max(lastSeq.current, ...res.messages.map((m) => m.seq));
        setMessages((current) => (initial ? res.messages : [...current, ...res.messages.filter((m) => !current.some((c) => c.id === m.id))]));
        if (res.messages.some((m) => !m.fromMySide)) markRead();
      }
    } catch (err) {
      if (initial && err instanceof ApiError && err.status === 404) setMissing(true);
    }
  }, [conversationId, markRead]);

  useEffect(() => {
    lastSeq.current = 0;
    setMessages([]);
    setMeta(null);
    setMissing(false);
    void pull(true);
    const timer = setInterval(() => document.visibilityState === 'visible' && void pull(false), 6000);
    return () => clearInterval(timer);
  }, [conversationId, pull]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  async function send(body: { body?: string; urgent?: boolean; quick?: string }) {
    setBusy(true);
    setNotice(null);
    try {
      const res = await api.post<{ message: ThreadMessage; masked: boolean }>(`/messages/conversations/${conversationId}/messages`, body);
      lastSeq.current = Math.max(lastSeq.current, res.message.seq);
      setMessages((current) => (current.some((m) => m.id === res.message.id) ? current : [...current, res.message]));
      if (res.masked) setNotice({ kind: 'info', text: 'Vos coordonnées (téléphone, e-mail, réseaux sociaux…) ont été masquées : extra et établissement échangent uniquement ici, dans l’application.' });
      if (body.body !== undefined) {
        setText('');
        setUrgent(false);
      }
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setNotice({ kind: 'error', text: MESSAGE_ERRORS[code] ?? 'Envoi impossible, réessayez.' });
      if (code === 'CONVERSATION_CLOSED') void pull(true);
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (text.trim()) void send({ body: text, urgent: urgent || undefined });
  }

  async function report(m: ThreadMessage) {
    const reason = window.prompt('Pourquoi signalez-vous ce message ? (insultes, harcèlement, tentative de contournement…)');
    if (!reason?.trim()) return;
    try {
      await api.post(`/messages/messages/${m.id}/report`, { reason });
      setNotice({ kind: 'info', text: 'Signalement envoyé au support Mon Extra, merci.' });
    } catch (err) {
      setNotice({ kind: 'error', text: err instanceof ApiError && err.code === 'ALREADY_REPORTED' ? 'Vous avez déjà signalé ce message.' : 'Signalement impossible.' });
    }
  }

  async function setBlocked(blocked: boolean) {
    if (blocked && !window.confirm('Bloquer cet interlocuteur ? Plus aucun message ne pourra être échangé entre vous, y compris sur d’autres missions.')) return;
    try {
      await api.put(`/messages/conversations/${conversationId}/block`, { blocked });
      await pull(true);
    } catch {
      setNotice({ kind: 'error', text: 'Action impossible.' });
    }
  }

  const header = meta?.counterpart ?? 'Conversation';
  const body = (
    <>
      {missing && <div className="error-banner">Cette conversation n’existe pas ou ne vous est pas accessible.</div>}

      {meta && (
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', display: 'flex', gap: 8, alignItems: 'flex-start', lineHeight: 1.5 }}>
          <ShieldCheck size={16} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden />
          <span>
            {meta.kind === 'MISSION'
              ? 'Les échanges se font uniquement ici : les numéros, e-mails et réseaux sociaux sont masqués automatiquement.'
              : 'Conversation avec l’équipe Mon Extra.'}
            {meta.mission && missionPath && <> Mission : <Link to={missionPath(meta.mission.id)} style={{ fontWeight: 700 }}>{meta.mission.title}</Link>.</>}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minHeight: embedded ? 220 : 0, maxHeight: embedded ? 420 : undefined, overflowY: embedded ? 'auto' : undefined }}>
        {messages.length === 0 && meta && <div className="empty-state">Aucun message pour l’instant. Écrivez le premier !</div>}
        {messages.map((m, i) => {
          const showDay = i === 0 || dayLabel(messages[i - 1].createdAt) !== dayLabel(m.createdAt);
          const support = m.side === 'SUPPORT';
          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {showDay && <div style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--text-tertiary)', fontWeight: 700, margin: '6px 0 2px', textTransform: 'capitalize' }}>{dayLabel(m.createdAt)}</div>}
              <div style={{ alignSelf: m.fromMySide ? 'flex-end' : 'flex-start', maxWidth: '86%' }}>
                <div
                  style={{
                    background: m.urgent ? 'var(--danger-tint)' : m.fromMySide ? 'var(--accent-tint)' : support ? 'var(--success-tint)' : 'var(--surface)',
                    border: '1px solid var(--border)',
                    borderRadius: 14,
                    padding: '8px 12px',
                    fontSize: 14,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {m.urgent && <div style={{ color: 'var(--danger)', fontWeight: 800, fontSize: 11.5, display: 'flex', gap: 4, alignItems: 'center', marginBottom: 2 }}><Zap size={12} /> URGENT</div>}
                  {m.body}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, textAlign: m.fromMySide ? 'right' : 'left', display: 'flex', gap: 8, justifyContent: m.fromMySide ? 'flex-end' : 'flex-start' }}>
                  <span>{m.author} · {clock(m.createdAt)}</span>
                  {!m.fromMySide && meta?.kind === 'MISSION' && (
                    <button type="button" onClick={() => report(m)} aria-label="Signaler ce message" style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--text-tertiary)', display: 'inline-flex', gap: 3, alignItems: 'center', fontSize: 11 }}>
                      <Flag size={11} /> Signaler
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottom} />
      </div>

      {notice && (notice.kind === 'info' ? <div className="badge badge-pending" style={{ padding: '10px 14px', whiteSpace: 'normal', lineHeight: 1.5 }}>{notice.text}</div> : <div className="error-banner">{notice.text}</div>)}

      {meta && !meta.open && (
        <div className="card" style={{ fontSize: 13.5, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div>{meta.closedReason === 'BLOCKED' ? (meta.blockedBy === meta.side ? 'Vous avez bloqué cet interlocuteur : plus aucun message ne peut être échangé.' : 'Cette conversation n’est plus disponible.') : CLOSED_TEXT[meta.closedReason ?? ''] ?? 'Cette conversation est fermée.'}</div>
          {meta.closedReason === 'BLOCKED' && meta.blockedBy === meta.side && <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setBlocked(false)}>Débloquer</button>}
        </div>
      )}

      {meta?.open && (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 8, position: embedded ? undefined : 'sticky', bottom: embedded ? undefined : 8, background: 'var(--bg)', paddingTop: 4 }}>
          {meta.quickReplies.length > 0 && (
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 6 }}>Jour J : réponses rapides</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {meta.quickReplies.map((q) => (
                  <button key={q.code} type="button" className={`btn btn-sm ${q.urgent ? 'btn-danger-outline' : 'btn-secondary'}`} disabled={busy} onClick={() => send({ quick: q.code })}>{q.text.replace(/\.$/, '')}</button>
                ))}
              </div>
            </div>
          )}
          <textarea className="input" rows={2} maxLength={2000} value={text} placeholder="Votre message…" aria-label="Votre message" onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); if (text.trim()) void send({ body: text, urgent: urgent || undefined }); } }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" disabled={busy || !text.trim()}>Envoyer</button>
            {meta.dayJ && (
              <label style={{ fontSize: 12.5, display: 'flex', gap: 4, alignItems: 'center', fontWeight: 700, color: urgent ? 'var(--danger)' : 'var(--text-secondary)' }}>
                <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} /> Urgent (prévient aussitôt par e-mail)
              </label>
            )}
            {meta.kind === 'MISSION' && (
              <button type="button" className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setBlocked(true)}><Ban size={13} /> Bloquer</button>
            )}
          </div>
        </form>
      )}
    </>
  );

  if (embedded) return <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>{body}</div>;
  return (
    <div className="app-shell" style={{ paddingBottom: 16 }}>
      <TopBar title={header} back />
      <div className="screen-content" style={{ gap: 12 }}>{body}</div>
    </div>
  );
}
